import { useState, useEffect, useCallback, useRef } from "react";

// ═══════════ DESIGN TOKENS ═══════════
const C = {
  bg:    "#0f1419",
  bg2:   "#131820",
  bg3:   "#1a212c",
  line:  "#232d3d",
  line2: "#2a3647",
  t1:    "#e8e5df",
  t2:    "#8f8c87",
  t3:    "#7a7773",
  t4:    "#252320",
  up:    "#c03828",
  down:  "#2e6fa8",
  neut:  "#5c5a56",
  accent:"#b8720e",
  gun:   "#c03828",
  wshed: "#8b4f9e",
  notable:"#2e7d52",
  common: "#3a3835",
  socmint: "#E8593C",
  adversary: "#3B8BD4",
  serif: "'Libre Baskerville', Georgia, serif",
  sans:  "system-ui, -apple-system, sans-serif",
  mono:  "'JetBrains Mono', 'Fira Mono', monospace",
};

const CLS_META = {
  gun:     { label:"Smoking Gun",  color: C.gun,    border:"#c03828" },
  watershed:{ label:"Watershed",  color: C.wshed,  border:"#8b4f9e" },
  notable: { label:"Notable",     color: C.notable, border:"#2e7d52" },
  common:  { label:"Common Ground",color: C.t3,    border:"#2d2b28" },
};

const WORKER_URL  = "https://desert-eye-v5-production.tompark100.workers.dev";
const GEMINI_KEYS = [
  process.env.REACT_APP_GEMINI_KEY_1,
  process.env.REACT_APP_GEMINI_KEY_2,
  process.env.REACT_APP_GEMINI_KEY_3,
].filter(Boolean);
const FLASH_MODEL = "gemini-2.5-flash";
const PRO_MODEL   = "gemini-2.5-flash";
const PROXY_URL   = process.env.REACT_APP_PROXY_URL || "";

async function fetchNewsFromWorker() {
  const res = await fetch(`${WORKER_URL}/news`);
  if (!res.ok) throw new Error(`Worker error ${res.status}`);
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || "News fetch failed");
  return data.articles || [];
}

async function callGemini(keys, model, prompt) {
  // Proxy 경로 (설정 시 우선 사용 — API 키 숨김)
  if (PROXY_URL) {
    for (let attempt = 0; attempt < 3; attempt++) {
      if (attempt > 0) await new Promise(r => setTimeout(r, 5000 * attempt));
      const res = await fetch(`${PROXY_URL}/api/gemini`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model, prompt }),
      });
      if (res.status === 429 || res.status === 503) continue;
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e?.error || `Proxy ${res.status}`); }
      const data = await res.json();
      return data.candidates?.[0]?.content?.parts?.[0]?.text || "";
    }
    throw new Error("quota_429");
  }

  // 직접 호출 fallback (PROXY_URL 미설정 시)
  let lastErr;
  for (const key of keys) {
    if (!key) continue;
    for (let attempt = 0; attempt < 3; attempt++) {
      if (attempt > 0) await new Promise(r => setTimeout(r, 5000 * attempt));
      const res = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${key}`,
        { method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ contents: [{ parts: [{ text: prompt }] }] }) }
      );
      if (res.status === 429 || res.status === 503) { lastErr = new Error(`quota_${res.status}`); continue; }
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e?.error?.message || `Gemini ${res.status}`); }
      const data = await res.json();
      return data.candidates?.[0]?.content?.parts?.[0]?.text || "";
    }
  }
  throw lastErr || new Error("No Gemini keys available");
}

function extractJSON(text) {
  const m = text.match(/```(?:json)?\s*([\s\S]*?)```/) || text.match(/(\[[\s\S]*\]|\{[\s\S]*\})/);
  return m ? (m[1] || m[0]).trim() : text.trim();
}

const CLS_CACHE_KEY = "desert_eye_cls_v2";
const CLS_CACHE_TTL = 24 * 60 * 60 * 1000;
const DEEP_CACHE_KEY = "desert_eye_deep_v5";
const DEEP_CACHE_TTL = 48 * 60 * 60 * 1000;

function loadClsCache() {
  try { const r = localStorage.getItem(CLS_CACHE_KEY); if (!r) return {}; const { data, ts } = JSON.parse(r); return Date.now() - ts > CLS_CACHE_TTL ? {} : data; } catch { return {}; }
}
function saveClsCache(c) {
  try { localStorage.setItem(CLS_CACHE_KEY, JSON.stringify({ data: c, ts: Date.now() })); } catch {}
}
function loadDeepCache() {
  try { const r = localStorage.getItem(DEEP_CACHE_KEY); if (!r) return null; const { data, ts } = JSON.parse(r); return Date.now() - ts > DEEP_CACHE_TTL ? null : data; } catch { return null; }
}
function saveDeepCache(d) {
  try { localStorage.setItem(DEEP_CACHE_KEY, JSON.stringify({ data: d, ts: Date.now() })); } catch {}
}

const COG_PROMPT = `You are an intelligence analyst. Classify each article using a CoG Semantic Routing Matrix. Return ONLY a JSON array (no markdown):
[{"id":"...", "cls":"gun|watershed|notable|common", "dir":"up|down|neutral", "reasoning":"1-sentence analyst note on the CoG signal", "hed_ko":"Korean translation of headline"}]

CLASSIFICATION RULES — distinguish SIGNAL (physical facts) from NOISE (rhetoric):
cls=gun: Physical/visual evidence of a CoG attack. Examples: strike on Kharg Island/South Pars/Natanz/Fordow, C2 blackout fragmenting IRGC command, sea mines blocking Strait of Hormuz, US pilot captured as PoW.
cls=watershed: CoG strike causing system failure OR major de-escalation. Examples: tankers abandoning Kharg, cascade blackout, proxy dead-hand strikes, ceasefire signed, Houthi/Hezbollah halting operations.
cls=notable: Tactical strike, localized damage, capability loss. Examples: single airbase hit, substation damaged, Houthi harassment, sanctions escalation.
cls=common: NOISE — rhetoric, threats, condemnations, routine posturing. Never upgrade rhetoric to gun/watershed.
dir=up: Physical attack, deployment, asset seizure. dir=down: Ceasefire, withdrawal, diplomatic deal. dir=neutral: Statements, no physical change.`;

async function classifyArticles(articles) {
  if (articles.length === 0 || GEMINI_KEYS.length === 0) return articles;
  const cache = loadClsCache();
  const uncached = articles.filter(a => !cache[a.id]);
  if (uncached.length > 0) {
    const BATCH = 12;
    const batches = [];
    for (let i = 0; i < uncached.length; i += BATCH) batches.push(uncached.slice(i, i + BATCH));
    for (let bi = 0; bi < batches.length; bi++) {
      if (bi > 0) await new Promise(r => setTimeout(r, 2500));
      const batch = batches[bi];
      const prompt = `${COG_PROMPT}\n\nArticles:\n${batch.map(a =>
        `{"id":"${a.id}","text":${JSON.stringify((a.hed + " " + (a.summary||"")).slice(0,200))}}`
      ).join("\n")}`;
      try {
        const text = await callGemini(GEMINI_KEYS, FLASH_MODEL, prompt);
        for (const c of JSON.parse(extractJSON(text))) {
          if (c.id) cache[c.id] = { cls: c.cls, dir: c.dir, reasoning: c.reasoning, hed_ko: c.hed_ko };
        }
      } catch {}
    }
    saveClsCache(cache);
  }
  return articles.map(a => cache[a.id] ? { ...a, ...cache[a.id] } : a);
}

async function fetchDeepAnalysis(articles) {
  if (GEMINI_KEYS.length === 0 || articles.length === 0) return { ach: [], scenarios: [], topArticles: [] };

  // 1. Worker KV 캐시 우선 조회
  try {
    const workerRes = await fetch(`${WORKER_URL}/deep`);
    if (workerRes.ok) {
      const workerData = await workerRes.json();
      if (workerData.ok && (workerData.ach?.length > 0 || workerData.scenarios?.length > 0)) {
        return { ach: workerData.ach || [], scenarios: workerData.scenarios || [], topArticles: workerData.topArticles || [] };
      }
    }
  } catch {}

  // 2. localStorage 캐시 확인
  const cached = loadDeepCache();
  if (cached) return cached;

  // 3. Gemini 직접 호출
  const signal = articles.filter(a => ["gun","watershed","notable"].includes(a.cls));
  const top = (signal.length >= 5 ? signal : articles).slice(0, 12);
  if (top.length === 0) return { ach: [], scenarios: [], topArticles: [] };
  const topArticles = top.map(a => ({ src: a.src, url: a.url, hed: a.hed }));
  const ctx = top.map(a => `- [${a.cls.toUpperCase()}] ${a.hed} (${a.src})`).join("\n");

  const combinedPrompt = `이란-미국-이스라엘 갈등 기사들을 분석하라. 반드시 다음 JSON 객체 하나만 반환 (마크다운 없이):
{
  "ach": [{"lens":"가설명","hed":"핵심 발견 한 문장","evidence":[{"tag":"SIGNAL|NOISE|DEESC","text":"근거 내용","src":"출처명(아래 기사 목록의 출처 이름 그대로)"}]}],
  "scenarios": [{"title":"시나리오 제목","probability":"높음|중간|낮음","reasoning":"2~3줄 근거"}]
}

ACH 규칙: SIGNAL(물리적 사실)과 NOISE(수사·성명)를 엄격히 구분하라. 5가지 가설: (1) 군사적 확전 경로, (2) 경제 전쟁, (3) 외교·진정 국면, (4) 대리세력 활성화, (5) 이란 내부 안정성. evidence의 src는 반드시 아래 기사 목록에 나온 출처명을 그대로 사용하라.

시나리오 규칙: 향후 72시간~2주 내 가장 가능성 있는 전개 방향 3가지를 구체적으로 제시하라.

기사:\n${ctx}`;

  let ach = [], scenarios = [];
  try {
    const text = await callGemini(GEMINI_KEYS, FLASH_MODEL, combinedPrompt);
    const parsed = JSON.parse(extractJSON(text));
    ach = Array.isArray(parsed.ach) ? parsed.ach : [];
    scenarios = Array.isArray(parsed.scenarios) ? parsed.scenarios : [];
  } catch {}

  const result = { ach, scenarios, topArticles };
  if (ach.length > 0 || scenarios.length > 0) {
    saveDeepCache(result);
    fetch(`${WORKER_URL}/deep`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(result),
    }).catch(() => {});
  }
  return result;
}

async function fetchSubScenarios(scenario, topArticles) {
  const ctx = topArticles.map(a => `- ${a.hed} (${a.src})`).join("\n");
  const prompt = `다음 시나리오를 더 세분화하라. 반드시 JSON 배열만 반환 (마크다운 없이):
[{"title":"세부 전개 제목","probability":"높음|중간|낮음","detail":"1~2줄 구체적 내용"}]
2~3개 항목.

시나리오: "${scenario.title}"
근거: ${scenario.reasoning}

관련 기사:\n${ctx}`;
  const text = await callGemini(GEMINI_KEYS, FLASH_MODEL, prompt);
  return JSON.parse(extractJSON(text));
}

async function fetchIdeas(input, articles) {
  if (GEMINI_KEYS.length === 0) throw new Error("Gemini API key missing");
  const ctx = articles.filter(a => a.cls !== "common").slice(0, 10)
    .map(a => `- ${a.hed} (${a.src}, ${a.cls})`).join("\n");
  const prompt = `당신은 탐사 저널리즘 어드바이저입니다. 현재 뉴스와 기자의 가설을 바탕으로 취재 아이디어를 한국어로 생성하라.
반드시 JSON 배열만 반환 (마크다운 없이):
[{"title":"기사 제목","missing":"주요 보도 공백","reasoning":"지금 이게 중요한 이유","actions":["취재 행동1","취재 행동2"],"proposal":"구체적 취재 방향"}]
3-5개 아이디어를 제시하라.

현재 뉴스:\n${ctx || "(관련 기사 없음)"}

기자 가설:\n${input}`;
  const text = await callGemini(GEMINI_KEYS, PRO_MODEL, prompt);
  return JSON.parse(extractJSON(text));
}

const Meta = ({ children, sx }) => (
  <span style={{ fontFamily:C.sans, fontSize:9, letterSpacing:"0.08em", textTransform:"uppercase", color:C.t3, ...(sx||{}) }}>
    {children}
  </span>
);

const SectionHead = ({ children }) => (
  <div style={{ padding:"20px 18px 8px", borderTop:`1px solid ${C.line2}` }}>
    <span style={{ fontFamily:C.sans, fontSize:13, fontWeight:800, letterSpacing:"0.12em", textTransform:"uppercase", color:C.t2 }}>
      {children}
    </span>
  </div>
);

const dirColor = d => d==="up" ? C.up : d==="down" ? C.down : C.neut;
const dirLabel = d => d==="up" ? "확전 요인" : d==="down" ? "진정 요인" : "중립";

function NewsCard({ article, onClick }) {
  const { hed, hed_ko, src, time, dir, cls, missing, priorSignals, reasoning, altSources, flow } = article;
  const cm = CLS_META[cls] || CLS_META.common;

  return (
    <div
      onClick={() => onClick(article)}
      style={{ padding:"14px 18px", borderBottom:`1px solid ${C.line}`, borderLeft:`3px solid ${cm.border}`, cursor:"pointer", transition:"background .1s" }}
      onMouseEnter={e => e.currentTarget.style.background = C.bg2}
      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
    >
      {/* Row 1: dir · cls */}
      <div style={{ display:"flex", alignItems:"center", gap:5, marginBottom:6 }}>
        <span style={{ fontSize:9, fontWeight:700, color:dirColor(dir), letterSpacing:"0.07em", textTransform:"uppercase" }}>{dirLabel(dir)}</span>
        <span style={{ color:C.t3, fontSize:9 }}>·</span>
        <span style={{ fontSize:9, fontWeight:600, color:cm.color, letterSpacing:"0.07em", textTransform:"uppercase" }}>{cm.label}</span>
      </div>

      {/* Headlines */}
      <div style={{ fontSize:14, fontWeight:700, color:C.t1, lineHeight:1.38, marginBottom:hed_ko ? 3 : 0, overflow:"hidden", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical" }}>
        {hed}
      </div>
      {hed_ko && (
        <div style={{ fontSize:13, color:"#a8a5a0", lineHeight:1.45, marginBottom:0, overflow:"hidden", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical" }}>
          {hed_ko}
        </div>
      )}

      {/* Bullet details */}
      {(reasoning || (missing && missing.length > 0) || (priorSignals && priorSignals.length > 0)) && (
        <div style={{ marginTop:8, display:"flex", flexDirection:"column", gap:4 }}>
          {reasoning && (
            <div style={{ display:"flex", gap:6, alignItems:"flex-start" }}>
              <span style={{ fontSize:9, color:C.accent, fontWeight:700, flexShrink:0, letterSpacing:"0.06em", paddingTop:2 }}>분석</span>
              <span style={{ fontSize:12, color:"#a8a5a0", lineHeight:1.55 }}>{reasoning}</span>
            </div>
          )}
          <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
            {missing && missing.length > 0 && (
              <>
                <span style={{ fontSize:9, color:C.t3, fontWeight:700, flexShrink:0, letterSpacing:"0.06em" }}>미확인</span>
                <span style={{ fontSize:9, color:C.t3 }}>{missing.join(" · ")}</span>
              </>
            )}
            <span style={{ fontSize:9, color:C.line2, flexShrink:0 }}>|</span>
            <span style={{ fontSize:9, fontWeight:600, color:C.t3 }}>{src}</span>
            {altSources?.map((s, i) => (
              <span key={i} style={{ fontSize:8, color:C.t2, background:C.bg3, padding:"1px 4px", border:`1px solid ${C.line2}` }}>+{s.src}</span>
            ))}
            <span style={{ fontSize:9, color:C.t3 }}>· {time}</span>
          </div>
          {priorSignals && priorSignals.length > 0 && (
            <div>
              <span style={{ fontSize:9, color:C.socmint, fontWeight:700, letterSpacing:"0.06em" }}>RUMINT 선행 {priorSignals.length}건</span>
              {priorSignals.map((p, i) => (
                <div key={i} style={{ display:"block", fontSize:10, color:C.t3, marginTop:2, paddingLeft:8, borderLeft:`1.5px solid ${C.socmint}` }}>
                  <span style={{ color:C.t2 }}>{p.src}</span> · {p.time} — {p.hed}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const THEME_DEFS = [
  { id:"nuclear",   label:"핵 협상",    keywords:["nuclear","uranium","natanz","enrichment","fordow","centrifuge","iaea","atomic"] },
  { id:"military",  label:"군사 충돌",  keywords:["airstrike","air strike","missile","drone","bombed","struck","idf","attack","fighter"] },
  { id:"diplomacy", label:"외교·협상", keywords:["ceasefire","deal","talks","agreement","diplomatic","negotiat","truce","summit"] },
  { id:"proxy",     label:"대리 세력", keywords:["houthi","hezbollah","hamas","proxy","red sea","rocket","lebanon","beirut"] },
  { id:"sanctions", label:"경제 제재", keywords:["sanction","embargo","oil","hormuz","export","tariff","price"] },
];

function getThemes(articles) {
  return THEME_DEFS.map(t => {
    const matches = articles.filter(a => {
      const txt = (a.hed + " " + (a.summary || "")).toLowerCase();
      return t.keywords.some(kw => txt.includes(kw));
    });
    return { ...t, count: matches.length, top: matches.slice(0, 2) };
  }).filter(t => t.count > 0).sort((a, b) => b.count - a.count).slice(0, 3);
}

function IdeaCard({ idea }) {
  const LBL = { fontSize:9, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:4, display:"block" };
  const BODY = { fontSize:12, color:C.t2, lineHeight:1.7 };
  return (
    <div style={{ padding:"16px 18px", borderBottom:`1px solid ${C.line}`, animation:"fadeUp .2s ease" }}>
      <div style={{ fontSize:14, fontWeight:700, color:C.t1, lineHeight:1.36, marginBottom:8 }}>{idea.title}</div>
      {idea.missing && (
        <div style={{ fontSize:11, color:C.up, marginBottom:8 }}>
          <span style={{ ...LBL, color:C.up, display:"inline" }}>보도 공백 </span>{idea.missing}
        </div>
      )}
      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        {idea.reasoning && (
          <div>
            <span style={{ ...LBL, color:C.accent }}>판단 근거</span>
            <div style={BODY}>{idea.reasoning}</div>
          </div>
        )}
        {idea.proposal && (
          <div>
            <span style={{ ...LBL, color:C.down }}>취재 제안</span>
            <div style={BODY}>{idea.proposal}</div>
          </div>
        )}
        {idea.actions?.length > 0 && (
          <div>
            <span style={{ ...LBL, color:C.notable }}>취재 행동</span>
            {idea.actions.map((a, j) => <div key={j} style={{ ...BODY, paddingLeft:8 }}>· {a}</div>)}
          </div>
        )}
      </div>
    </div>
  );
}

export default function DesertEye() {
  const [tab, setTab] = useState("news");
  const [time, setTime] = useState(new Date());

  const [news, setNews]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [lastFetch, setLastFetch] = useState(null);

  const [ach, setAch]                   = useState([]);
  const [scenarios, setScenarios]       = useState([]);
  const [topArticles, setTopArticles]   = useState([]);
  const [subScenariosMap, setSubScenariosMap]   = useState({});
  const [subLoadingIdx, setSubLoadingIdx]       = useState(null);
  const [deepLoading, setDeepLoading]   = useState(false);
  const [deepError, setDeepError]       = useState("");

  const [sel, setSel] = useState(null);

  const [ideaIn, setIdeaIn]     = useState("");
  const [ideas, setIdeas]       = useState(null);
  const [ideaL, setIdeaL]       = useState(false);
  const [ideaErr, setIdeaErr]   = useState("");
  const [autoIdeas, setAutoIdeas]   = useState(null);
  const [autoIdeaL, setAutoIdeaL]   = useState(false);

  const [unverifiedPanel, setUnverifiedPanel] = useState("socmint");

  const panelRef     = useRef(null);
  const deepDoneRef  = useRef(false);
  const ideaDoneRef  = useRef(false);
  const newsRef      = useRef([]);

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const runDeep = useCallback(async (articles) => {
    if (deepDoneRef.current) return;
    deepDoneRef.current = true;
    setDeepLoading(true); setDeepError("");
    try {
      const { ach: achResult, scenarios: scenResult, topArticles: taResult } = await fetchDeepAnalysis(articles);
      if (Array.isArray(achResult)) setAch(achResult);
      if (Array.isArray(scenResult)) setScenarios(scenResult);
      if (Array.isArray(taResult)) setTopArticles(taResult);
    } catch (e) {
      const msg = e.message || "";
      setDeepError(msg.includes("quota_429") || msg.includes("quota_503")
        ? "오늘 AI 분석 사용량 초과. 캐시된 분석이 없으면 나중에 다시 시도해주세요."
        : msg);
    }
    setDeepLoading(false);
  }, []);

  const runIdeas = useCallback(async (articles) => {
    if (ideaDoneRef.current) return;
    ideaDoneRef.current = true;
    setAutoIdeaL(true);
    try {
      const r = await fetchIdeas("현재 이란-미국-이스라엘 갈등에서 독자가 주목해야 할 핵심 취재 각도와 스쿠프 가능성", articles);
      if (r?.length > 0) setAutoIdeas(r);
    } catch {}
    setAutoIdeaL(false);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    setAch([]);
    setScenarios([]);
    setTopArticles([]);
    setSubScenariosMap({});
    setSubLoadingIdx(null);
    setDeepError("");
    setAutoIdeas(null);
    setAutoIdeaL(false);
    deepDoneRef.current = false;
    ideaDoneRef.current = false;

    try {
      const raw = await fetchNewsFromWorker();
      setNews(raw);
      setLastFetch(new Date());
      setLoading(false);

      if (raw.length > 0) {
        // Phase 2: classify with Gemini Flash (top 50 only to conserve quota)
        const classified = await classifyArticles(raw.slice(0, 50)).catch(() => raw);
        const merged = [...classified, ...raw.slice(50)];
        setNews(merged);
        newsRef.current = merged;
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const genIdeas = async () => {
    if (!ideaIn.trim()) return;
    setIdeaL(true); setIdeas(null); setIdeaErr("");
    try {
      const result = await fetchIdeas(ideaIn.trim(), news);
      if (result && result.length > 0) setIdeas(result);
      else setIdeaErr("응답 없음");
    } catch (err) { setIdeaErr(err.message); }
    setIdeaL(false);
  };

  const mainNews = news.filter(n => n.origin === "rsshub");
  const byTier = {
    gun:      mainNews.filter(n => n.cls === "gun"),
    watershed:mainNews.filter(n => n.cls === "watershed"),
    notable:  mainNews.filter(n => n.cls === "notable"),
    common:   mainNews.filter(n => n.cls === "common"),
  };
  const TIER_ORDER = [
    { key:"gun",       label:"Smoking Gun" },
    { key:"watershed", label:"Watershed / Game Changer" },
    { key:"notable",   label:"Notable Update" },
    { key:"common",    label:"Common Ground" },
  ];

  const CLS_RANK = { gun: 0, watershed: 1, notable: 2, common: 3 };
  const byImportance = arr => [...arr].sort((a, b) => (CLS_RANK[a.cls] ?? 3) - (CLS_RANK[b.cls] ?? 3));
  const socmint = byImportance(news.filter(n => n.origin === "telegram" && ["osint", "socmint"].includes(n.type)));
  const adversary = byImportance(news.filter(n => n.origin === "telegram" && n.type === "adversary"));

  const sw = id => {
    setTab(id);
    if (panelRef.current) panelRef.current.scrollTop = 0;
    if (id === "deeper") { if (!deepDoneRef.current) setDeepLoading(true); setTimeout(() => runDeep(newsRef.current), 4000); }
    if (id === "idea")   { if (!ideaDoneRef.current) setAutoIdeaL(true);  setTimeout(() => runIdeas(newsRef.current), 4000); }
  };

  return (
    <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", background:C.bg, color:C.t1, fontFamily:C.sans }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&family=JetBrains+Mono:wght@400&display=swap');
        *,*::before,*::after{box-sizing:border-box}
        a{color:inherit;text-decoration:none}
        button{font-family:inherit;border-radius:0}
        ::-webkit-scrollbar{width:3px}
        ::-webkit-scrollbar-track{background:${C.bg}}
        ::-webkit-scrollbar-thumb{background:${C.t4}}
        @keyframes blink{0%,100%{opacity:1}50%{opacity:.15}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:none}}
        @keyframes spin{to{transform:rotate(360deg)}}
      `}</style>

      {/* ── HEADER ── */}
      <div style={{ padding:"12px 18px 10px", borderBottom:`1px solid ${C.line}`, flexShrink:0 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
          <div style={{ fontFamily:C.serif, fontSize:20, fontWeight:700, letterSpacing:"-0.01em", lineHeight:1 }}>
            Desert Eye
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:6, paddingTop:2 }}>
            {loading && <span style={{ fontSize:8, fontFamily:C.mono, color:C.accent, animation:"blink 1s infinite", letterSpacing:"0.1em" }}>LIVE</span>}
            <div style={{ width:5, height:5, borderRadius:"50%", background: loading ? C.accent : error ? C.up : "#3d7a52", boxShadow: loading ? `0 0 6px ${C.accent}` : error ? `0 0 6px ${C.up}` : `0 0 6px #3d7a52` }} />
          </div>
        </div>
        <div style={{ marginTop:6, display:"flex", gap:14, alignItems:"center", flexWrap:"wrap" }}>
          {[
            { city:"Washington", tz:"America/New_York" },
            { city:"Tel Aviv",   tz:"Asia/Jerusalem" },
            { city:"Tehran",     tz:"Asia/Tehran" },
          ].map(({ city, tz }) => (
            <div key={tz} style={{ display:"flex", gap:4, alignItems:"baseline" }}>
              <span style={{ fontFamily:C.mono, fontSize:9, color:C.t3, letterSpacing:"0.06em" }}>{city}</span>
              <span style={{ fontFamily:C.mono, fontSize:10, color:C.t2, letterSpacing:"0.06em" }}>
                {time.toLocaleTimeString("en-US", { timeZone:tz, hour:"2-digit", minute:"2-digit", hour12:false })}
              </span>
            </div>
          ))}
          {lastFetch && (
            <span style={{ fontFamily:C.mono, fontSize:8, color:C.t3, marginLeft:"auto" }}>
              {lastFetch.toLocaleTimeString("ko-KR",{hour:"2-digit",minute:"2-digit"})} 갱신
            </span>
          )}
        </div>
      </div>

      {/* ── TABS ── */}
      <div style={{ display:"flex", borderBottom:`1px solid ${C.line}`, flexShrink:0 }}>
        {[["news","#NEWS"],["unverified","#UNVERIFIED"],["deeper","#DEEPER"],["idea","#IDEA"]].map(([id,label]) => (
          <button key={id} onClick={() => sw(id)} style={{
            flex:1, padding:"9px 0", background:"none", border:"none", cursor:"pointer",
            fontSize:9, fontWeight: tab===id ? 800 : 500,
            letterSpacing:"0.14em", textTransform:"uppercase",
            color: tab===id ? C.t1 : C.t3,
            borderBottom: tab===id ? `1.5px solid ${C.t1}` : "1.5px solid transparent",
            transition:"color .12s"
          }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── PANEL ── */}
      <div ref={panelRef} style={{ flex:1, overflowY:"auto", minHeight:0 }}>

        {/* ════════ #NEWS ════════ */}
        {tab === "news" && <div>
          {loading && news.length === 0 && (
            <div style={{ padding:"50px 18px", textAlign:"center" }}>
              <div style={{ width:16, height:16, border:`1.5px solid ${C.t4}`, borderTop:`1.5px solid ${C.t2}`, borderRadius:"50%", animation:"spin 1s linear infinite", margin:"0 auto 12px" }} />
              <div style={{ fontSize:11, color:C.t3 }}>뉴스 수집 중…</div>
            </div>
          )}

          {error && (
            <div style={{ padding:"16px 18px", borderBottom:`1px solid ${C.line}` }}>
              <div style={{ fontSize:11, color:C.accent, marginBottom:6 }}>수집 실패</div>
              <div style={{ fontFamily:C.mono, fontSize:8, color:C.t3, background:C.bg2, padding:"8px", wordBreak:"break-all", whiteSpace:"pre-wrap", marginBottom:10 }}>{error}</div>
              <button onClick={() => load()} style={{ background:"none", border:`1px solid ${C.line2}`, color:C.t2, fontSize:10, padding:"7px 14px", cursor:"pointer" }}>재시도</button>
            </div>
          )}

          {/* ── 테마 위젯 ── */}
          {news.length > 0 && (() => {
            const themes = getThemes(news);
            if (themes.length === 0) return null;
            return (
              <div style={{ padding:"12px 18px", borderBottom:`1px solid ${C.line2}`, background:C.bg2 }}>
                <Meta sx={{ display:"block", marginBottom:8 }}>타매체 집중 테마</Meta>
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {themes.map(th => (
                    <div key={th.id}>
                      <div style={{ display:"flex", alignItems:"baseline", gap:6, marginBottom:4 }}>
                        <span style={{ fontSize:11, fontWeight:700, color:C.t1, letterSpacing:"0.04em" }}>{th.label}</span>
                        <span style={{ fontSize:9, color:C.t3 }}>{th.count}건</span>
                      </div>
                      {th.top.map((a, i) => (
                        <div key={i} onClick={() => setSel(a)} style={{ fontSize:11, color:C.t2, lineHeight:1.45, paddingLeft:8, borderLeft:`1.5px solid ${C.line2}`, marginBottom:3, cursor:"pointer" }}>
                          · {a.hed_ko || a.hed}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {TIER_ORDER.filter(t => byTier[t.key].length > 0).map(tier => (
            <div key={`list-${tier.key}`}>
              <div style={{ padding:"8px 18px 4px", display:"flex", alignItems:"center", gap:8 }}>
                <div style={{ width:6, height:6, borderRadius:"50%", background: CLS_META[tier.key]?.border, flexShrink:0 }} />
                <span style={{ fontFamily:"system-ui", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:CLS_META[tier.key]?.color }}>
                  {tier.label}
                </span>
                <span style={{ fontSize:9, color:C.t3, marginLeft:2 }}>{byTier[tier.key].length}</span>
              </div>
              {byTier[tier.key].map(n => <NewsCard key={n.id} article={n} onClick={a => setSel(a)} />)}
            </div>
          ))}

          {!loading && news.length > 0 && (
            <div style={{ padding:"20px 18px", textAlign:"center" }}>
              <button onClick={() => load()}
                style={{ background:"none", border:`1px solid ${C.line2}`, color:C.t3, fontSize:10, padding:"8px 20px", cursor:"pointer", letterSpacing:"0.1em" }}>
                새로고침
              </button>
            </div>
          )}
        </div>}

        {/* ════════ #UNVERIFIED ════════ */}
        {tab === "unverified" && <div>
          <div style={{ display:"flex", borderBottom:`1px solid ${C.line}` }}>
            {[["socmint", "SOCMINT"], ["adversary", "Adversary News"]].map(([id, label]) => (
              <button key={id} onClick={() => setUnverifiedPanel(id)} style={{
                flex:1, padding:"9px 0", background:"none", border:"none", cursor:"pointer",
                fontSize:10, fontWeight: unverifiedPanel===id ? 700 : 400,
                letterSpacing:"0.1em", textTransform:"uppercase",
                color: unverifiedPanel===id ? (id === "socmint" ? C.socmint : C.adversary) : C.t3,
                borderBottom: unverifiedPanel===id ? `2px solid ${id === "socmint" ? C.socmint : C.adversary}` : "2px solid transparent",
              }}>
                {label}
              </button>
            ))}
          </div>

          {unverifiedPanel === "socmint" && (
            <div>
              <SectionHead>Telegram OSINT Channels</SectionHead>
              {socmint.length === 0 ? (
                <div style={{ padding:"30px 18px", textAlign:"center" }}>
                  <Meta>데이터 로딩 중…</Meta>
                </div>
              ) : (
                socmint.map(n => <NewsCard key={n.id} article={n} onClick={a => setSel(a)} />)
              )}
            </div>
          )}

          {unverifiedPanel === "adversary" && (
            <div>
              <SectionHead>적대세력 공식 미디어 (이란·중국·러시아)</SectionHead>
              {adversary.length === 0 ? (
                <div style={{ padding:"30px 18px", textAlign:"center" }}>
                  <Meta>데이터 로딩 중…</Meta>
                </div>
              ) : (
                adversary.map(n => <NewsCard key={n.id} article={n} onClick={a => setSel(a)} />)
              )}
            </div>
          )}
        </div>}

        {/* ════════ #DEEPER ════════ */}
        {tab === "deeper" && <div>
          {deepLoading && (
            <div style={{ padding:"30px 18px", textAlign:"center" }}>
              <div style={{ width:14, height:14, border:`1.5px solid ${C.t4}`, borderTop:`1.5px solid ${C.t2}`, borderRadius:"50%", animation:"spin 1s linear infinite", margin:"0 auto 10px" }} />
              <div style={{ fontSize:11, color:C.t3 }}>심화 분석 중…</div>
            </div>
          )}

          {deepError && (
            <div style={{ padding:"14px 18px", borderBottom:`1px solid ${C.line}` }}>
              <div style={{ fontSize:11, color:C.accent, marginBottom:6 }}>심화 분석 실패</div>
              <div style={{ fontSize:11, color:C.t3 }}>{deepError}</div>
            </div>
          )}

          {!deepLoading && ach.length === 0 && scenarios.length === 0 && !deepError && (
            <div style={{ padding:"30px 18px", textAlign:"center" }}>
              <Meta>분석 데이터 없음 — 새로고침 후 재시도</Meta>
            </div>
          )}

          {/* 시나리오 예측 */}
          {scenarios.length > 0 && (
            <div>
              <div style={{ padding:"10px 18px 6px", display:"flex", alignItems:"center", gap:8 }}>
                <div style={{ width:6, height:6, borderRadius:"50%", background:C.wshed, flexShrink:0 }} />
                <span style={{ fontFamily:"system-ui", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:C.wshed }}>시나리오 예측</span>
              </div>
              {scenarios.map((s, i) => {
                const probColor = s.probability === "높음" ? C.up : s.probability === "중간" ? C.accent : C.t3;
                const subList = subScenariosMap[i];
                const isSubLoading = subLoadingIdx === i;
                return (
                  <div key={i} style={{ padding:"14px 18px", borderBottom:`1px solid ${C.line}`, animation:"fadeUp .2s ease" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                      <div style={{ fontSize:14, fontWeight:700, color:C.t1, lineHeight:1.32, flex:1 }}>{s.title}</div>
                      <span style={{ fontSize:9, fontWeight:700, color:probColor, letterSpacing:"0.08em" }}>{s.probability}</span>
                    </div>
                    <div style={{ fontSize:12, color:C.t2, lineHeight:1.65, marginBottom:10 }}>{s.reasoning}</div>

                    {/* 서브 시나리오 */}
                    {subList?.map((sub, j) => {
                      const sc = sub.probability === "높음" ? C.up : sub.probability === "중간" ? C.accent : C.t3;
                      return (
                        <div key={j} style={{ marginBottom:8, paddingLeft:10, borderLeft:`2px solid ${C.line2}` }}>
                          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                            <div style={{ fontSize:12, fontWeight:700, color:C.t1, flex:1 }}>{sub.title}</div>
                            <span style={{ fontSize:8, fontWeight:700, color:sc, letterSpacing:"0.06em" }}>{sub.probability}</span>
                          </div>
                          {sub.detail && <div style={{ fontSize:11, color:C.t2, lineHeight:1.55 }}>{sub.detail}</div>}
                        </div>
                      );
                    })}

                    {/* More 버튼 */}
                    {!subList && !isSubLoading && (
                      <button
                        onClick={async () => {
                          setSubLoadingIdx(i);
                          try {
                            const subs = await fetchSubScenarios(s, topArticles);
                            setSubScenariosMap(prev => ({ ...prev, [i]: subs }));
                          } catch {}
                          setSubLoadingIdx(null);
                        }}
                        style={{ background:"none", border:`1px solid ${C.line2}`, color:C.t3, fontSize:9, padding:"4px 12px", cursor:"pointer", letterSpacing:"0.08em" }}
                      >
                        More
                      </button>
                    )}
                    {isSubLoading && (
                      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                        <div style={{ width:9, height:9, border:`1.5px solid ${C.t4}`, borderTop:`1.5px solid ${C.t2}`, borderRadius:"50%", animation:"spin 1s linear infinite" }} />
                        <span style={{ fontSize:10, color:C.t3 }}>분석 중…</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ACH 분석 */}
          {ach.length > 0 && (
            <div>
              <div style={{ padding:"10px 18px 6px", display:"flex", alignItems:"center", gap:8 }}>
                <div style={{ width:6, height:6, borderRadius:"50%", background:C.accent, flexShrink:0 }} />
                <span style={{ fontFamily:"system-ui", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:C.accent }}>ACH 분석</span>
              </div>
              {ach.map((a, i) => (
                <div key={i} style={{ padding:"16px 18px", borderBottom:`1px solid ${C.line}`, animation:"fadeUp .2s ease" }}>
                  <Meta sx={{ color:C.accent }}>{a.lens}</Meta>
                  <div style={{ fontFamily:C.sans, fontSize:14, fontWeight:700, color:C.t1, lineHeight:1.36, margin:"6px 0 10px" }}>
                    {a.hed}
                  </div>
                  {(a.evidence || []).map((ev, j) => {
                    const evArticle = ev.src ? topArticles.find(ta => ta.src === ev.src) : null;
                    return (
                      <div key={j} style={{ marginBottom:8, paddingLeft:10, borderLeft:`1.5px solid ${C.line2}`, display:"flex", gap:8, alignItems:"flex-start" }}>
                        {ev.tag && (
                          <span style={{ flexShrink:0, fontFamily:C.mono, fontSize:8, color:C.accent, background:C.bg2, padding:"2px 5px", marginTop:2 }}>
                            {ev.tag}
                          </span>
                        )}
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:12, color:C.t2, lineHeight:1.65 }}>{ev.text}</div>
                          {ev.src && (
                            evArticle?.url
                              ? <a href={evArticle.url} target="_blank" rel="noopener noreferrer"
                                  style={{ fontSize:9, color:C.t3, textDecoration:"none", borderBottom:`1px solid ${C.line2}` }}
                                  onClick={e => e.stopPropagation()}>
                                  {ev.src}
                                </a>
                              : <span style={{ fontSize:9, color:C.t3 }}>{ev.src}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>}

        {/* ════════ #IDEA ════════ */}
        {tab === "idea" && <div>
          {/* 가설 입력 */}
          <div style={{ padding:"14px 18px", borderBottom:`1px solid ${C.line}` }}>
            <Meta sx={{ display:"block", marginBottom:8 }}>가설 입력 — 직접 취재 각도 분석</Meta>
            <div style={{ display:"flex", gap:8 }}>
              <input value={ideaIn} onChange={e => setIdeaIn(e.target.value)} onKeyDown={e => e.key==="Enter" && genIdeas()}
                placeholder="취재 각도 또는 가설…"
                style={{ flex:1, background:C.bg2, border:`1px solid ${C.line2}`, color:C.t1, fontSize:13, padding:"10px 12px", outline:"none" }} />
              <button onClick={genIdeas} disabled={ideaL} style={{ background:C.t1, border:"none", color:C.bg, fontSize:11, fontWeight:800, padding:"10px 16px", cursor: ideaL ? "wait" : "pointer", opacity: ideaL ? 0.5 : 1 }}>GO</button>
            </div>
          </div>

          {ideaL && (
            <div style={{ padding:"24px 18px", textAlign:"center" }}>
              <div style={{ width:14, height:14, border:`1.5px solid ${C.t4}`, borderTop:`1.5px solid ${C.t2}`, borderRadius:"50%", animation:"spin 1s linear infinite", margin:"0 auto 10px" }} />
              <div style={{ fontSize:11, color:C.t3 }}>교차 분석 중…</div>
            </div>
          )}
          {ideaErr && (
            <div style={{ padding:"12px 18px", borderBottom:`1px solid ${C.line}` }}>
              <div style={{ fontSize:10, color:C.accent }}>{ideaErr}</div>
            </div>
          )}

          {/* 사용자 가설 결과 */}
          {(ideas || []).map((idea, i) => <IdeaCard key={`u${i}`} idea={idea} />)}

          {/* 자동 생성 아이디어 (기본) */}
          {autoIdeaL && !ideas && (
            <div style={{ padding:"24px 18px", textAlign:"center" }}>
              <div style={{ width:14, height:14, border:`1.5px solid ${C.t4}`, borderTop:`1.5px solid ${C.t2}`, borderRadius:"50%", animation:"spin 1s linear infinite", margin:"0 auto 10px" }} />
              <div style={{ fontSize:11, color:C.t3 }}>취재 아이디어 생성 중…</div>
            </div>
          )}
          {!ideas && autoIdeas && (
            <>
              <div style={{ padding:"14px 18px", borderBottom:`1px solid ${C.line2}` }}>
                <Meta sx={{ color:C.t3 }}>AI 추천 취재 아이디어 — 현재 기사 기반 자동 생성</Meta>
              </div>
              {autoIdeas.map((idea, i) => <IdeaCard key={`a${i}`} idea={idea} />)}
            </>
          )}
          {!ideas && !autoIdeas && !autoIdeaL && !ideaL && (
            <div style={{ padding:"40px 18px", textAlign:"center" }}>
              <Meta>뉴스가 로드되면 취재 아이디어를 자동으로 생성합니다</Meta>
            </div>
          )}
        </div>}

      </div>

      {/* ── STATUS BAR ── */}
      <div style={{ height:20, background:C.bg, borderTop:`1px solid ${C.line}`, display:"flex", alignItems:"center", flexShrink:0, overflow:"hidden" }}>
        {[
          loading ? "LIVE" : `${news.length}건`,
          lastFetch ? lastFetch.toLocaleTimeString("ko-KR",{hour:"2-digit",minute:"2-digit"}) : null,
          deepLoading ? "분석중…" : ach.length > 0 ? `ACH ${ach.length}` : null,
          "v5.0"
        ].filter(Boolean).map((s, i) => (
          <span key={i} style={{ fontFamily:C.mono, fontSize:7, color:C.t3, padding:"0 8px", borderRight:`1px solid ${C.line}`, whiteSpace:"nowrap", letterSpacing:"0.1em" }}>
            {s}
          </span>
        ))}
      </div>

      {/* ── ARTICLE DETAIL SHEET ── */}
      {sel && (
        <div
          onClick={e => e.target === e.currentTarget && setSel(null)}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.85)", zIndex:600, display:"flex", alignItems:"flex-end", justifyContent:"center" }}
        >
          <div style={{ background:C.bg3, borderTop:`1px solid ${C.line2}`, width:"100%", maxWidth:540, maxHeight:"82vh", overflowY:"auto", borderRadius:"8px 8px 0 0", animation:"fadeUp .18s ease" }}>
            <div style={{ position:"sticky", top:0, background:C.bg3, borderBottom:`1px solid ${C.line}`, padding:"13px 18px", display:"flex", gap:12, alignItems:"flex-start" }}>
              <div style={{ flex:1 }}>
                <div style={{ marginBottom:4 }}>
                  <span style={{ fontSize:9, fontWeight:700, letterSpacing:"0.09em", textTransform:"uppercase", color: dirColor(sel.dir) }}>
                    {dirLabel(sel.dir)}
                  </span>
                  {sel.cls && (
                    <span style={{ marginLeft:8, fontSize:11, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:CLS_META[sel.cls]?.color || C.t3 }}>
                      {CLS_META[sel.cls]?.label}
                    </span>
                  )}
                </div>
                <div style={{ fontSize:16, fontWeight:700, color:C.t1, lineHeight:1.32 }}>{sel.hed}</div>
                <div style={{ display:"flex", alignItems:"center", gap:5, flexWrap:"wrap", marginTop:4 }}>
                  <span style={{ fontSize:10, fontWeight:600, color:C.t2 }}>{sel.src}</span>
                  {sel.altSources?.map((s, i) => (
                    <span key={i} style={{ fontSize:8, color:C.t3, background:C.bg2, padding:"1px 5px", border:`1px solid ${C.line2}` }}>+{s.src}</span>
                  ))}
                  <span style={{ fontSize:9, color:C.t3 }}>· {sel.time}</span>
                </div>
              </div>
              <button onClick={() => setSel(null)} style={{ background:"none", border:"none", color:C.t3, fontSize:18, cursor:"pointer", padding:0, lineHeight:1, flexShrink:0 }}>✕</button>
            </div>

            <div style={{ padding:"16px 18px" }}>
              {sel.reasoning && (
                <div style={{ marginBottom:12 }}>
                  <Meta sx={{ display:"block", marginBottom:5, color:C.accent }}>분석 노트</Meta>
                  <div style={{ fontSize:12, color:C.t2, lineHeight:1.7 }}>{sel.reasoning}</div>
                </div>
              )}
              <Meta sx={{ display:"block", marginBottom:7 }}>요약</Meta>
              <div style={{ fontSize:13, color:C.t2, lineHeight:1.72, marginBottom:14 }}>
                {sel.summary || "요약 없음"}
              </div>

              {(sel.priorSignals?.length > 0 || sel.altSources?.length > 0) && (
                <div style={{ marginBottom:14 }}>
                  {/* 정보 유통 경로 */}
                  {sel.priorSignals?.length > 0 && (
                    <div style={{ marginBottom:8 }}>
                      <Meta sx={{ display:"block", marginBottom:5, color:C.t3 }}>정보 유통 경로</Meta>
                      <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap", fontSize:10, color:C.t3 }}>
                        {sel.priorSignals.map((p, i) => (
                          <span key={i} style={{ color:C.socmint, fontWeight:700 }}>{p.src}</span>
                        ))}
                        <span>→</span>
                        <span style={{ color:C.t2, fontWeight:600 }}>{sel.src}</span>
                        {sel.altSources?.map((s, i) => (
                          <span key={i} style={{ color:C.t2 }}>/ {s.src}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* RUMINT 원문 */}
                  <Meta sx={{ display:"block", marginBottom:7, color:C.socmint }}>RUMINT 선행 신호 {sel.priorSignals?.length}건</Meta>
                  {sel.priorSignals?.map((p, i) => (
                    <a key={i} href={p.url} target="_blank" rel="noopener noreferrer"
                      style={{ display:"block", fontSize:11, color:C.t2, padding:"6px 10px", borderLeft:`2px solid ${C.socmint}`, marginBottom:5, textDecoration:"none", background:C.bg2 }}>
                      <span style={{ color:C.socmint, fontSize:9, fontWeight:700 }}>{p.src}</span> · {p.time}
                      <div style={{ color:C.t3, fontSize:10, marginTop:2 }}>{p.hed}</div>
                    </a>
                  ))}
                </div>
              )}

              {sel.url && (
                <a href={sel.url} target="_blank" rel="noopener noreferrer"
                  style={{ display:"block", padding:"11px 16px", background:C.bg2, border:`1px solid ${C.line2}`, textAlign:"center", fontSize:12, fontWeight:700, color:C.t1, letterSpacing:"0.06em" }}>
                  원문 보기 →
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}